import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import { muiTheme } from "@/styles/theme";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  TextField,
} from "@mui/material";
import { Edit, Check, X, Trash2, Plus } from "lucide-react";
import {
  formatBudget,
  formatDate,
  getValidityStatus,
} from "@/modules/lead-page/utils";
import { useManageReferrals } from "@/modules/lead-page/hooks/manage-referrals.hook";
import AddReferral from "./add-referral";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

const ManageReferrals = () => {
  const {
    // State
    openLeadReferralsModal,
    setOpenAddReferralModal,
    openAddReferralModal,
    editingValidityIdx,
    validityDraft,

    // Data
    referralsData,
    isLoadingReferrals,

    // Loading states
    isUpdatingValidity,
    isDeletingReferral,

    // Handlers
    handleOpenModal,
    handleCloseModal,
    handleStartEditing,
    handleCancelEditing,
    handleValidityChange,
    handleSaveValidity,
    handleDeleteReferral,
  } = useManageReferrals();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: nawyTheme.nawy.spacing.xs,
          padding: nawyTheme.nawy.spacing.xs,
          alignItems: "center",
          width: "100%",
          height: "100%",
          cursor: "pointer",
          ":hover": {
            backgroundColor: nawyTheme.nawy.colors.semantic.layer.secondary,
          },
        }}
        onClick={handleOpenModal}
      >
        <Typography
          sx={{
            color: nawyTheme.nawy.colors.semantic.text.primary,
            ...nawyTheme.nawy.typography.english.variants.body.md.regular,
          }}
        >
          Manage Referrals
        </Typography>
      </Box>

      <ResponsiveEditDialog
        open={openLeadReferralsModal}
        setOpen={handleCloseModal}
        header="Manage Referrals"
        hideModalFooter
        modalFooter={false}
        width={1000}
      >
        <Box sx={{ width: "100%" }}>
          {/* Table Container */}
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "white",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                width: "100%",
                marginBottom: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  padding: "8px 14px",
                  backgroundColor:
                    muiTheme.colorSchemes.light.palette.main.actionDisabled,
                  borderRadius: 2,
                  border: "1px solid #D0D5DD",
                  color: " #344054",
                  boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                  "&:hover": {
                    background:
                      muiTheme.colorSchemes.light.palette.background.white,
                    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                  },
                  "&:active": {
                    boxShadow:
                      "0px 0px 0px 4px #F2F4F7, 0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                  },
                }}
                onClick={() => {
                  setOpenAddReferralModal(true);
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      ...muiTheme.typography.h5_bold,
                      color: muiTheme.colorSchemes.light.palette.primary[500],
                      textTransform: "none",
                    }}
                  >
                    <Plus size={14} />
                    Add
                  </Typography>
                </Box>
              </Button>
            </Box>

            {/* Table Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1.2fr 1.2fr 1fr 80px",
                borderBottom: `1px solid ${muiTheme.colorSchemes.light.palette.neutral[100]}`,
                gap: 2,
                px: 3,
                py: 2,
              }}
            >
              <Typography
                sx={{
                  color: muiTheme.colorSchemes.light.palette.neutral[700],
                  fontWeight: 600,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                Referrer Name
              </Typography>
              <Typography
                sx={{
                  color: muiTheme.colorSchemes.light.palette.neutral[700],
                  fontWeight: 600,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                Units
              </Typography>
              <Typography
                sx={{
                  color: muiTheme.colorSchemes.light.palette.neutral[700],
                  fontWeight: 600,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                Budget
              </Typography>
              <Typography
                sx={{
                  color: muiTheme.colorSchemes.light.palette.neutral[700],
                  fontWeight: 600,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                Referral Date
              </Typography>
              <Typography
                sx={{
                  color: muiTheme.colorSchemes.light.palette.neutral[700],
                  fontWeight: 600,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                Validity
              </Typography>
              <Typography
                sx={{
                  color: muiTheme.colorSchemes.light.palette.neutral[700],
                  fontWeight: 600,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                Actions
              </Typography>
            </Box>

            {/* Table Rows */}
            {isLoadingReferrals ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              referralsData?.data?.map((row, idx) => {
                const validityStatus = getValidityStatus(
                  row.validityExpirationDate,
                  row.createdAt
                );
                const isEditing = editingValidityIdx === idx;

                return (
                  <Box
                    key={row.uid}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1.2fr 1.2fr 1fr 80px",
                      gap: 2,
                      px: 3,
                      py: 2.5,
                      alignItems: "center",
                      borderBottom: `1px solid ${muiTheme.colorSchemes.light.palette.neutral[100]}`,
                    }}
                  >
                    {/* Name */}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: muiTheme.colorSchemes.light.palette.neutral[900],
                        opacity: validityStatus.expired ? 0.3 : 1,
                      }}
                    >
                      {row.referrer.name}
                    </Typography>

                    {/* Units */}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,

                        color: muiTheme.colorSchemes.light.palette.neutral[900],
                        opacity: validityStatus.expired ? 0.5 : 1,
                      }}
                    >
                      {row.numberOfUnits ?? "—"}
                    </Typography>

                    {/* Budget */}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: muiTheme.colorSchemes.light.palette.neutral[900],
                        opacity: validityStatus.expired ? 0.5 : 1,
                      }}
                    >
                      {formatBudget(row.budget)}
                    </Typography>

                    {/* Referral Date */}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: muiTheme.colorSchemes.light.palette.neutral[700],
                        opacity: validityStatus.expired ? 0.5 : 1,
                      }}
                    >
                      {row.validityExpirationDate
                        ? formatDate(row.validityExpirationDate, false)
                        : "—"}
                    </Typography>

                    {/* Validity */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {isEditing ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <TextField
                            type="number"
                            value={validityDraft !== null ? validityDraft : ""}
                            onChange={(e) =>
                              handleValidityChange(e.target.value)
                            }
                            placeholder="Days"
                            size="small"
                            inputProps={{
                              min: 0,
                              max: 365,
                            }}
                            sx={{
                              minWidth: 80,
                              "& .MuiInputBase-root": {
                                fontSize: "13px",
                              },
                            }}
                          />
                          <IconButton
                            onClick={() => handleSaveValidity(row.uid)}
                            disabled={
                              isUpdatingValidity ||
                              validityDraft === null ||
                              validityDraft < 0
                            }
                            size="small"
                            sx={{
                              color:
                                muiTheme.colorSchemes.light.palette.success
                                  .main,
                              "&:hover": {
                                backgroundColor:
                                  muiTheme.colorSchemes.light.palette
                                    .success[50],
                              },
                              "&:disabled": {
                                color:
                                  muiTheme.colorSchemes.light.palette
                                    .neutral[300],
                              },
                            }}
                          >
                            {isUpdatingValidity ? (
                              <CircularProgress
                                size={14}
                                sx={{
                                  color:
                                    muiTheme.colorSchemes.light.palette.success
                                      .main,
                                }}
                              />
                            ) : (
                              <Check size={14} />
                            )}
                          </IconButton>
                          <IconButton
                            onClick={handleCancelEditing}
                            size="small"
                            sx={{
                              color:
                                muiTheme.colorSchemes.light.palette.error.main,
                              "&:hover": {
                                backgroundColor:
                                  muiTheme.colorSchemes.light.palette.error[50],
                              },
                            }}
                          >
                            <X size={14} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: validityStatus.color,
                          }}
                        >
                          {validityStatus.text}
                          <IconButton
                            onClick={() =>
                              handleStartEditing(
                                idx,
                                row.validityExpirationDate
                              )
                            }
                            size="small"
                            sx={{
                              color:
                                muiTheme.colorSchemes.light.palette
                                  .primary[500],
                              "&:hover": {
                                backgroundColor:
                                  muiTheme.colorSchemes.light.palette
                                    .primary[50],
                              },
                            }}
                          >
                            <Edit size={14} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <IconButton
                        onClick={() => handleDeleteReferral(row.uid)}
                        disabled={isDeletingReferral}
                        size="small"
                        sx={{
                          color: muiTheme.colorSchemes.light.palette.error.main,
                          "&:hover": {
                            backgroundColor:
                              muiTheme.colorSchemes.light.palette.error[50],
                          },
                          "&:disabled": {
                            color:
                              muiTheme.colorSchemes.light.palette.neutral[300],
                          },
                        }}
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
          <AddReferral
            open={openAddReferralModal}
            setOpen={setOpenAddReferralModal}
          />
        </Box>
      </ResponsiveEditDialog>
    </>
  );
};

export default ManageReferrals;
